import type { ReactElement } from 'react';
import React from 'react';
import {
  Button,
  Form,
  FormField,
  FormRow,
  FormSection,
  FormTitle,
  Input,
  ModalFooter,
  Select,
  Text,
  View,
} from '../ui';
import { useForm } from 'react-hook-form';
import * as zod from 'zod';
import startCase from 'lodash/startCase';
import type { LostContractFragment, LostContractInput } from '../../graphql';
import { Currency, PaymentUnit } from '../../graphql';
import LoadingSpinner from '../LoadingSpinner';
import {
  nullableStrToEnum,
  stringToNumber,
  valueOrNull,
} from '../../lib/zod-util';
import { paymentUnitsToOptions } from '../../lib/form-util';
import { currencies, paymentUnits } from '../../constants/paymentUnits';
import { stringToNumOpt } from '../core/util';
import type { ScenarioOrder } from '../../lib/planningData';
import { useSaveLostContract } from '../voyageCharter/lostContracts';
import { getOrderDisplayName } from '../order/order';
import type { LostContractModalResult } from './LostContractModal';

const _lostContractSchema = zod.object({
  remarks: zod.string(),
  freightCurrency: nullableStrToEnum(Currency),
  freightAmount: zod.number().nullable(),
  freightUnit: zod.nativeEnum(PaymentUnit).nullable(),
});

type LostContractFormFields = zod.infer<typeof _lostContractSchema>;

export type LostContractFormProps = {
  onComplete: (result: LostContractModalResult) => void;
  onCancel: () => void;
  orders?: Pick<
    ScenarioOrder,
    'id' | 'incomingVoyageCharterRelation' | 'orderDetailsByOrderId'
  >[];
  lostContract?: LostContractFragment;
};

const LostContractForm = ({
  onComplete,
  onCancel,
  orders,
  lostContract,
}: LostContractFormProps): ReactElement => {
  if (!orders && !lostContract)
    throw new Error(
      `Orders or existing Lost contract must be defined to LostContractForm`,
    );

  const {
    handleSubmit,
    formState: { errors },
    register,
    setFocus,
  } = useForm<LostContractFormFields>({
    defaultValues: {
      remarks: lostContract?.remarks ?? '',
      freightAmount: stringToNumOpt(lostContract?.freightAmount),
      freightUnit: lostContract?.freightUnit ?? null,
      freightCurrency: lostContract?.freightCurrency ?? null,
    },
  });

  const {
    isLoading,
    errors: saveErrors,
    saveLostContract,
  } = useSaveLostContract();
  const errorKeys = Object.keys(errors);
  // const saveErrors = saveError || updateError;

  const onSubmit = async (data: LostContractFormFields) => {
    const input: LostContractInput = {
      // From form
      remarks: data.remarks,
      freightAmount: data.freightAmount,
      freightUnit: data.freightUnit,
      freightCurrency: data.freightCurrency,
    };

    if (lostContract) {
      const result = await saveLostContract(null, lostContract.id, input);
      if (!result.errors?.length) {
        onComplete(true);
      }
    } else if (orders) {
      const result = await saveLostContract(orders, null, input);
      if (!result.errors?.length) {
        onComplete(true);
      }
    }
  };

  return (
    <View height="100%">
      <Form
        style={{ overflowY: 'visible', borderBottom: 'none' }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormSection
          borderBottomStyle="none"
          marginTop={2}
          marginLeft={-2}
          marginBottom={-3}
        >
          <FormTitle>
            <Text fontSize="1.4em" fontWeight="bold">
              {lostContract ? 'Edit lost contract' : 'New lost contract'}
            </Text>
          </FormTitle>
        </FormSection>
        <FormSection zIndex={4} borderBottomStyle="none">
          <FormRow>
            <FormField label="Orders" width="100%">
              <Text>
                {(orders ?? []).map((o) => getOrderDisplayName(o)).join(', ')}
              </Text>
            </FormField>
          </FormRow>
        </FormSection>
        <FormSection zIndex={4} borderBottomStyle="none">
          <FormRow>
            <FormField
              label="Remarks"
              width="100%"
              required
              hasError={!!errors.remarks}
            >
              <Input
                {...register('remarks')}
                placeholder="Remarks about competition etc..."
                type="text"
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Freight amount" width={200}>
              <Input
                step="0.01"
                type="number"
                {...register(`freightAmount`, {
                  setValueAs: stringToNumber,
                })}
                onChange={() => setFocus(`freightAmount`)}
              />
            </FormField>
            <FormField label="Freight Unit" width={200}>
              <Select
                {...register(`freightUnit`, {
                  setValueAs: valueOrNull,
                })}
                options={paymentUnitsToOptions(paymentUnits)}
                enablePlaceholder
                placeholder="-"
              />
            </FormField>
            <FormField
              label="Freight currency"
              width={200}
              hasError={!!errors.freightCurrency}
            >
              <Select
                {...register('freightCurrency', {
                  setValueAs: valueOrNull,
                })}
                options={paymentUnitsToOptions(currencies)}
                placeholder="-"
                enablePlaceholder
              />
            </FormField>
          </FormRow>
        </FormSection>

        {errorKeys.length > 0 && (
          <FormSection borderBottomStyle="none">
            <Text color="#9d0300">
              {errorKeys
                .map((key) => `${startCase(key)} is not valid.`)
                .join(' ')}
            </Text>
          </FormSection>
        )}

        {isLoading && (
          <FormSection borderBottomStyle="none">
            <View flexDirection="row">
              <Text mr={3}>Saving changes</Text>
              <LoadingSpinner />
            </View>
          </FormSection>
        )}

        {saveErrors && (
          <FormSection borderBottomStyle="none">
            <Text color="#9d0300">
              {saveErrors.map((e) => `${e.name}: ${e.message}`).join(' \n')}
            </Text>
          </FormSection>
        )}
      </Form>

      <div style={{ flexGrow: 1 }} />

      <ModalFooter>
        {onCancel && (
          <Button
            variant="negative"
            disabled={isLoading}
            onPress={() => onCancel()}
            marginRight="3"
          >
            Discard changes & Close
          </Button>
        )}
        <div style={{ flexGrow: 1 }} />
        <Button disabled={isLoading} onPress={handleSubmit(onSubmit)}>
          {'Save & Close'}
        </Button>
      </ModalFooter>
    </View>
  );
};

export default LostContractForm;
